import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { getPropertyItemByUpid, initialState, PropertyItem, State } from 'app/core/properties-table/properties.state';
import { AsyncItem } from 'app/shared/async-item';
import { groups } from './groups';
import {
    addParcels,
    clearParcels,
    favorColumns,
    removeParcels,
    selectColumns,
    setLtsaCredentialsNotification,
    startTasks,
    taskFail,
    taskResult
} from './properties.actions';
import { Column } from './table.model';


const dynamicInitial = (selectedColumns: string[]) =>
    Object.fromEntries(groups.flatMap(g => g.columns
        .filter(c => selectedColumns.includes(c.key))
        .map(c => [c.key, new AsyncItem(null, 'new', c.type)])
    ));


export const parcelReducer = createReducer(
    initialState,
    on(addParcels, (state, { parcels }) => ({
        ...state,
        selectedColumns: state.properties.length == 0 ? [] : state.selectedColumns,
        properties: [
            ...state.properties,
            ...parcels
                .filter(p => !state.properties.find(item => item.parcel.upid === p.upid))
                .map(parcel => ({
                    parcel,
                    table: dynamicInitial(state.properties.length == 0 ? [] : state.selectedColumns),
                })),
        ],
    })),
    on(removeParcels, (state, { upids }) => ({
        ...state,
        properties: state.properties.filter(item => !upids.includes(item.parcel.upid)),
    })),
    on(clearParcels, state => ({
        ...state,
        loaded: false,
        properties: [],
    })),
    on(selectColumns, (state, { columnKeys }) => ({
        ...state,
        selectedColumns: columnKeys,
        loaded: true,
        properties: state.properties.map(item => ({
            ...item,
            table: dynamicInitial(columnKeys),
        })),
    })),
    on(favorColumns, (state, { columnKeys }) => ({
        ...state,
        favoriteColumns: columnKeys,
    })),
    on(startTasks, (state, { taskNames, upids }) => ({
        ...state,
        loaded: true,
        properties: state.properties.map(item => {
            if (!upids.includes(item.parcel.upid)) {
                // if property not scheduled for task, return as it is
                return item;
            }
            return {
                ...item,
                table: {
                    ...item.table,
                    ...Object.fromEntries(taskNames
                        .flatMap(taskName => groups.filter(g => g.taskName === taskName))
                        .flatMap(g => g.columns.map(c => [c.key, new AsyncItem(null, 'loading', c.type)]))
                    ),
                },
            };
        }),
    })),
    on(taskResult, (state, { taskName, dynamic }) => ({
        ...state,
        properties: state.properties.map(item => {
            const propertyData = dynamic.find(value => item.parcel.upid === value.upid);
            if (!propertyData) {
                return item;
            }
            const makeAsyncItem = (c: Column) => propertyData.status
                ? new AsyncItem(propertyData.values[c.key], 'done', c.type)
                : new AsyncItem(propertyData.message, 'error', c.type);

            const updateTableItem = (c: Column) => item.table[c.key]?.state === 'done' ? item.table[c.key] : makeAsyncItem(c);

            return {
                ...item,
                table: {
                    ...item.table,
                    ...Object.fromEntries(groups.filter(g => g.taskName === taskName)
                        .flatMap(g => g.columns.map(c => [c.key, updateTableItem(c)]))
                    ),
                },
            };
        }),
    })),
    on(taskFail, (state, { taskName, upids, reason }) => ({
        ...state,
        properties: state.properties.map(item => {
            if (!upids.includes(item.parcel.upid)) {
                return item;
            }

            const fail = (c: Column) =>
                item.table[c.key]?.state === 'loading' ? new AsyncItem(reason, 'error', c.type) : item.table[c.key];

            return {
                ...item,
                table: {
                    ...item.table,
                    ...Object.fromEntries(groups.filter(g => g.taskName === taskName)
                        .flatMap(g => g.columns.map(c => [c.key, fail(c)]))
                    ),
                },
            };
        }),
    })),
    on(setLtsaCredentialsNotification, (state, { value }) => ({
        ...state,
        showLtsaBadCredentialsToast: value,
    }))
);

const base = createFeatureSelector<State>('properties');

/**
 * property is complete when all of selected columns are not in `loading` or `new` - means either done, error or timeout
 */
const isCompleteTable = (table: { [key: string]: AsyncItem }, selectedColumns: string[]) =>
    !selectedColumns.some(key => table[key] && (table[key].state === 'loading' || table[key].state === 'new'));

const calcCompletedProperties = (items: PropertyItem[], selectedColumns: string[]) =>
    items.reduce((acc, item) => acc + (isCompleteTable(item.table, selectedColumns) ? 1 : 0), 0);

export const getSelectedColumns = createSelector(base, state => state.loaded ? state.selectedColumns : []);
export const getFavoriteColumns = createSelector(base, state => state.favoriteColumns);

export const getProperties = createSelector(base, state => state.properties);
export const getCompletedProperties = createSelector(base, state => calcCompletedProperties(state.properties, state.selectedColumns));

export const getReportByUpid = (upid: string) => createSelector(getPropertyItemByUpid(upid), item => item?.report);

export const getReportById = (reportId: string) =>
    createSelector(base, state => state.properties.find(p => p.report?.id === reportId)?.report);

export const getPreset = (reportId: string, dataSourceKey: string) =>
    createSelector(getReportById(reportId), report => report?.presets[dataSourceKey]);
